import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Download, FileSpreadsheet, Pencil, Search } from "lucide-react";
import type { ModificationReportRow, ModificationReportRowKind } from "../../types";
import { getApi } from "../../api";
import { useAsyncResource } from "../../hooks/useAsyncResource";
import { useDebounce } from "../../hooks/useDebounce";
import { reportError } from "../../services/eventBus";
import { Button } from "../Button";
import { Card } from "../Card";
import { DataGrid, type GridColumn } from "../DataGrid";
import { IconButton } from "../IconButton";
import { Pagination } from "../Pagination";
import { EmptyState, ErrorState } from "../ScreenStates";
import { TagPill } from "../Pills";
import { inputClass } from "../fields/fieldStyles";
import { exportModificationsWorkbook } from "../../utils/workbooks";
import { editorPath } from "../../utils/actuarialPaths";
import { formatDate } from "../../utils/format";

type KindFilter = "All" | ModificationReportRowKind;
const KIND_FILTERS: readonly KindFilter[] = ["All", "Custom Condition", "Custom Rating", "Debit Modification"];
const PAGE_SIZE = 20;

/**
 * Landing table for Customize Conditions: every custom condition, custom rating, and debit modification, newest first,
 * searchable, filterable by type, 20 per page. The API does the filtering and paging; the screen only ever holds one page
 * (the seeded catalog carries about 2,000 modifications, and loading them all made this the slowest screen in the app).
 * Clicking a row opens the editor on that rating.
 */
export function CustomizationsTable() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<KindFilter>("All");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const search = useDebounce(query.trim(), 250);
  const kind = filter === "All" ? undefined : filter;
  const report = useAsyncResource(() => getApi().listModifications({ kind, search: search || undefined, page, pageSize: PAGE_SIZE }), `customizations|${filter}|${search}|${page}`, "CustomizationsTable.load");
  const rows = report.data?.items ?? [];
  const totalCount = report.data?.totalCount ?? 0;

  const columns = useMemo<GridColumn<ModificationReportRow>[]>(
    () => [
      { key: "kind", header: "Type", widthClassName: "whitespace-nowrap", render: (row) => <TagPill label={row.kind} tone={row.kind === "Debit Modification" ? "warning" : "green"} /> },
      {
        key: "condition",
        header: "Condition",
        widthClassName: "whitespace-nowrap",
        render: (row) => (
          <Link to={editorPath({ conditionId: row.conditionId, ratingId: null })} className="font-medium text-brand-navy underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-brand-green-50 rounded">
            {row.conditionName}
          </Link>
        ),
      },
      {
        key: "rating",
        header: "Rating",
        render: (row) =>
          row.ratingText ? (
            <Link to={editorPath(row)} className="text-brand-navy underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-brand-green-50 rounded">
              {row.ratingText}
            </Link>
          ) : (
            <span className="text-gray-400">–</span>
          ),
      },
      { key: "detail", header: "Detail", render: (row) => <span className="block max-w-md truncate text-gray-700" title={row.detail}>{row.detail}</span> },
      { key: "author", header: "Author", widthClassName: "whitespace-nowrap", render: (row) => row.authorName },
      { key: "createdUtc", header: "Date", widthClassName: "whitespace-nowrap", render: (row) => formatDate(row.createdUtc) },
    ],
    [],
  );

  /** The workbook carries every row that matches the current filter and search, not just the page on screen. */
  const handleExport = async () => {
    try {
      const everything = await getApi().listModifications({ kind, search: search || undefined, page: 1, pageSize: 0 });
      exportModificationsWorkbook(everything.items);
    } catch (error) {
      reportError(error, "CustomizationsTable.export");
    }
  };

  return (
    <Card
      title="Existing customizations"
      icon={FileSpreadsheet}
      actions={
        <Button variant="secondary" size="sm" icon={Download} onClick={() => void handleExport()} disabled={totalCount === 0}>
          Export .xlsx
        </Button>
      }
    >
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[18rem] flex-1">
          <label htmlFor="customizations-search" className="sr-only">
            Search customizations
          </label>
          <input
            id="customizations-search"
            type="search"
            value={query}
            onChange={(event) => {
              setQuery(event.currentTarget.value);
              setPage(1);
            }}
            placeholder="Search by condition, rating, notes, or author"
            autoComplete="off"
            className={`${inputClass()} pl-10`}
          />
          <Search size={18} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
        </div>
        <div role="radiogroup" aria-label="Filter by type" className="flex flex-wrap gap-2">
          {KIND_FILTERS.map((option) => (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={filter === option}
              onClick={() => {
                setFilter(option);
                setPage(1);
              }}
              className={`min-h-[36px] cursor-pointer rounded-full border px-3 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-brand-green-50 ${filter === option ? "border-brand-green bg-brand-green-10 text-brand-green-dark" : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"}`}
            >
              {option}
            </button>
          ))}
        </div>
        <span className="text-xs text-gray-500" aria-live="polite">
          {report.data ? `${totalCount} customization${totalCount === 1 ? "" : "s"}` : ""}
        </span>
      </div>
      {report.state.kind === "error" && <ErrorState message={report.state.message} onRetry={report.reload} />}
      {report.state.kind !== "error" && (
        <>
          <DataGrid
            caption="Existing customizations"
            columns={columns}
            rows={rows}
            rowKey={(row) => row.id}
            isLoading={report.state.kind === "loading"}
            density="compact"
            actionsHeader="Customization actions"
            renderActions={(row) => <IconButton icon={Pencil} label={`Edit ${row.ratingText ?? row.conditionName}`} size="sm" onClick={() => navigate(editorPath(row))} />}
            emptyState={<EmptyState message={search || filter !== "All" ? "No customizations match this search." : "No customizations recorded yet. Use New Custom Condition or Modify Existing Condition to add one."} />}
          />
          <Pagination page={page} pageSize={PAGE_SIZE} totalCount={totalCount} onPageChange={setPage} />
        </>
      )}
    </Card>
  );
}
